import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Pencil, Trash2, Clock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_COLOR = {
  Lecture: "bg-blue-50 border-blue-300 text-blue-800",
  Task: "bg-[#F7FEE7] border-[#76C121] text-[#365314]",
  Meeting: "bg-purple-50 border-purple-300 text-purple-800",
  Deadline: "bg-red-50 border-red-300 text-red-800",
  Personal: "bg-slate-50 border-slate-300 text-slate-700",
};

const PRIORITY_BORDER = { High: "border-l-4 border-l-red-500", Medium: "border-l-4 border-l-amber-500", Low: "border-l-4 border-l-slate-400" };

const ROW_HEIGHT = 52;

function toMinutes(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

export default function DayView({ date, events, availableSlots, onAdd, onEdit, onRefresh }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 7 * ROW_HEIGHT;
  }, [date]);

  const cycleStatus = async (ev) => {
    const order = ["Pending", "In Progress", "Completed"];
    const next = order[(order.indexOf(ev.status) + 1) % order.length];
    try {
      await api.put(`/schedule/${ev.id}`, { status: next });
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/schedule/${id}`);
      toast.success("Removed");
      onRefresh();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg" data-testid="schedule-day-list">
        <div className="px-5 py-3.5 border-b border-[#E2E8F0] flex items-center justify-between">
          <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">{date}</h3>
          <Button size="sm" onClick={onAdd} data-testid="schedule-add-item-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
          </Button>
        </div>
        <div ref={scrollRef} className="overflow-y-auto" style={{ maxHeight: 640 }} data-testid="schedule-day-timeline">
          <div className="relative" style={{ height: 24 * ROW_HEIGHT }}>
            {Array.from({ length: 24 }).map((_, h) => (
              <div key={h} className="absolute left-0 right-0 border-t border-[#F1F5F9]" style={{ top: h * ROW_HEIGHT }}>
                <span className="absolute -top-2 left-2 text-[10px] text-[#94A3B8] bg-white pr-1.5">{String(h).padStart(2, "0")}:00</span>
              </div>
            ))}
            <div className="absolute left-16 right-3 top-0 bottom-0">
              {events.length === 0 && (
                <p className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-sm text-[#4B5563]">Nothing scheduled for this day.</p>
              )}
              {events.map((ev) => {
                const start = toMinutes(ev.start_time);
                const end = ev.end_time ? toMinutes(ev.end_time) : start + 30;
                const top = (start / 60) * ROW_HEIGHT;
                const height = Math.max(((end - start) / 60) * ROW_HEIGHT, 26);
                return (
                  <div
                    key={ev.id}
                    data-testid={`schedule-event-${ev.id}`}
                    onClick={() => onEdit(ev)}
                    style={{ top, height }}
                    className={`absolute left-0 right-0 rounded-md border px-2.5 py-1 cursor-pointer overflow-hidden group ${CATEGORY_COLOR[ev.category]} ${ev.priority ? PRIORITY_BORDER[ev.priority] : ""}`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <p className="text-xs font-semibold truncate">{ev.title}</p>
                      <div className="hidden group-hover:flex items-center gap-1.5 shrink-0">
                        <button data-testid={`schedule-edit-${ev.id}`} onClick={(e) => { e.stopPropagation(); onEdit(ev); }}><Pencil className="h-3 w-3" /></button>
                        <button data-testid={`schedule-delete-${ev.id}`} onClick={(e) => remove(ev.id, e)}><Trash2 className="h-3 w-3 text-red-600" /></button>
                      </div>
                    </div>
                    <p className="text-[10px] opacity-80 truncate">{ev.start_time}{ev.end_time ? `–${ev.end_time}` : ""}</p>
                    {ev.category !== "Lecture" && (
                      <button data-testid={`schedule-status-${ev.id}`} onClick={(e) => { e.stopPropagation(); cycleStatus(ev); }} className="mt-0.5">
                        <StatusBadge value={ev.status} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-lg" data-testid="schedule-available-slots">
        <div className="px-5 py-3.5 border-b border-[#E2E8F0] flex items-center gap-2">
          <Clock className="h-4 w-4 text-[#00823B]" />
          <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">Available Slots</h3>
        </div>
        <div className="p-3 space-y-2 max-h-[600px] overflow-y-auto">
          {(availableSlots || []).length === 0 && <p className="text-xs text-[#4B5563] text-center py-4">No free slots (full day booked).</p>}
          {(availableSlots || []).map((s, i) => (
            <div key={i} data-testid={`available-slot-${i}`} className="text-xs bg-[#F7FEE7] border border-[#76C121]/30 rounded-md px-3 py-2 font-medium text-[#00823B]">
              {s.start} – {s.end}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

